"use client";

import { Button, cn } from "@saridana-core/react-ui-kit";
import { TOurTeamPerson, TOurTeamSection } from "@shared/types";
import { HtmlParser, SectionHeading } from "@shared/ui";
import Image from "next/image";
import { useState } from "react";

function TeamPersonCard({ name, avatar, description, position, rtl }: TOurTeamPerson & { rtl?: boolean }) {
  return (
    <div
      className="rounded-2xl flex flex-col-reverse md:flex-row items-start overflow-hidden"
      style={{
        background: "linear-gradient(270deg, #F1FAFE 0%, #F6FAFD 100%)",
      }}
    >
      <div className={cn("py-14 lg:py-[100px] px-8 md:px-[60px] md:w-[55%} md:max-w-[55%]", rtl ? "md:order-2" : "md:order-1")}>
        <div className="space-y-4">
          <h4 className="text-4xl md:text-5xl font-bold text-primary-700">{name}</h4>
          {position && <p className="text-xl md:text-2xl font-semibold">{position}</p>}
        </div>
        <div className="mt-8 text-lg md:text-xl">
          <HtmlParser data={description} />
        </div>
      </div>
      {avatar && avatar.data && (
        <div className={cn("flex-1 relative self-center md:self-end", rtl ? "md:order-1" : "md:order-2")}>
          <div
            className={cn(
              "relative md:absolute sm:-bottom-12 lg:-bottom-24 h-auto w-full md:h-[45rem] md:w-[45rem]",
              rtl ? "md:-left-52 lg:left-2" : "md:-right-52 lg:right-2",
            )}
          >
            <div
              className="absolute md:hidden z-2 h-full w-full"
              style={{ background: "linear-gradient(180deg, transparent 80%, #F6FAFD 100%)" }}
            ></div>
            <Image
              src={avatar.data?.attributes?.url || ""}
              alt={avatar.data?.attributes?.alternativeText || avatar?.data?.attributes?.name || ""}
              width={avatar?.data?.attributes?.width || 590}
              height={avatar?.data?.attributes?.height || 590}
              className="z-3 w-full h-auto object-contains"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function OurTeamSection({ title, description, team }: TOurTeamSection) {
  const [currentTab, setCurrentTab] = useState<number>(0);

  return (
    <section className="py-[80px] sm:py-[100px] lg:py-[120px] relative overflow-hidden bg-white">
      <div className="container relative">
        <SectionHeading
          className="text-center"
          title={title}
          titleClassName="mx-auto lg:max-w-3xl leading-[112%]"
          description={description}
          descriptionClassName="leading-[134%]"
        />
        {team && team.length > 0 && (
          <div className="mt-12">
            <div className="flex gap-3 w-full mb-14 md:mb-[60px] items-center sm:justify-center overflow-x-auto">
              {team.map((team, index) => {
                return (
                  <Button
                    key={"team-nav-" + team.title + "-" + index}
                    variant="ghost"
                    className={cn(
                      "text-lg md:text-xl font-semibold rounded-3xl px-6 h-10 md:h-12",
                      currentTab === index ? "bg-primary-50 text-primary-600" : "text-gray-600 bg-gray-50",
                    )}
                    onClick={() => setCurrentTab(index)}
                  >
                    {team.title}
                  </Button>
                );
              })}
            </div>
            {team[currentTab] && team[currentTab].persons && team[currentTab].persons.length > 0 && (
              <div
                className="space-y-10"
                key={"team-content-" + currentTab}
                style={{
                  animation: "fadeIn 0.4s ease-in-out",
                }}
              >
                {team[currentTab].persons.map((person, index) => {
                  return <TeamPersonCard key={"team-person-" + index} {...person} rtl={index % 2 !== 0} />;
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
